import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../API/axios";
import { people } from "../../config/imagesLink";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import PersonSharpIcon from "@mui/icons-material/PersonSharp";
import { useTranslation } from "react-i18next";

const ChatUserDetailsPanel = ({
  show,
  onClose,
  chatInfo,
  isOnline,
}) => {
  const { t } = useTranslation();
  const language = localStorage.getItem("Lang");
  const sessionId = localStorage.getItem("sessionId");
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const userUid = chatInfo?.toUserUid;

  useEffect(() => {
    if (!show || !userUid) return undefined;

    let cancelled = false;
    setIsLoading(true);

    axios
      .post("UserProfile/GetUserProfile", {
        deviceType: "Web",
        userUid,
        sessionId,
        langKey: language === "en" ? "" : language,
      })
      .then((response) => {
        if (cancelled) return;
        setProfile(response.data?.responseData?.profileInfo || null);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, userUid, sessionId, language]);

  if (!show || !userUid) return null;

  const info = profile || {};
  const displayName =
    info.firstName && info.lastName
      ? `${info.firstName} ${info.lastName}`
      : chatInfo?.toUserFullName || "";
  const avatar =
    info.profilPic || chatInfo?.toUserImage || people.DefaultProfile;
  const online = isOnline ?? chatInfo?.toUserIsOnline === true;

  return (
    <>
      <div className="chat-user-details-backdrop" onClick={onClose} />
      <aside className="chat-user-details-panel" aria-label={t("Notifications.Contact info")}>
        <div className="chat-user-details-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0 f-14 f-600">{t("Notifications.Contact info")}</h6>
          <button
            type="button"
            className="chat-user-details-close"
            onClick={onClose}
            aria-label={t("Common.Close")}
          >
            <CloseSharpIcon className="material-symbols-sharp" />
          </button>
        </div>

        <div className="chat-user-details-body">
          <div className="chat-user-details-identity text-center py-3">
            <div className="chat-user-details-avatar-wrap">
              <img
                src={avatar}
                alt={displayName}
                width={96}
                height={96}
                className="chat-user-details-avatar"
                onError={(e) => {
                  e.target.src = people.DefaultProfile;
                }}
              />
            </div>
            <h5 className="chat-user-details-name f-16 f-600 mt-3 mb-1">
              {displayName}
            </h5>
            <div className="chat-user-details-status d-flex align-items-center justify-content-center gap-1">
              <span
                className={`chat-user-details-status-dot${online ? " is-online" : " is-offline"}`}
                aria-hidden="true"
              />
              <span className="chat-user-details-status-text f-12 f-400">
                {online
                  ? t("Notifications.Online")
                  : t("Notifications.Offline")}
              </span>
            </div>
          </div>

          {isLoading ? (
            <p className="text-center f-12 text-muted py-2">
              {t("Loading")}
            </p>
          ) : (
            <div className="chat-user-details-list">
              {info.headLine ? (
                <div className="chat-user-details-row">
                  <span className="chat-user-details-label f-12">
                    {t("User Profile.Profile Headline")}
                  </span>
                  <span className="chat-user-details-value f-12">
                    {info.headLine}
                  </span>
                </div>
              ) : null}

              {info.title ? (
                <div className="chat-user-details-row">
                  <span className="chat-user-details-label f-12">
                    {t("User Profile.Title")}
                  </span>
                  <span className="chat-user-details-value f-12">
                    {info.title}
                  </span>
                </div>
              ) : null}

              {info.cityName || info.countryName ? (
                <div className="chat-user-details-row">
                  <span className="chat-user-details-label f-12">
                    {t("User Profile.City")}
                  </span>
                  <span className="chat-user-details-value f-12">
                    {[info.cityName, info.countryName].filter(Boolean).join(", ")}
                  </span>
                </div>
              ) : null}

              {info.about ? (
                <div className="chat-user-details-row">
                  <span className="chat-user-details-label f-12">
                    {t("User Profile.About")}
                  </span>
                  <span className="chat-user-details-value f-12">
                    {info.about.length > 120
                      ? `${info.about.slice(0, 120)}...`
                      : info.about}
                  </span>
                </div>
              ) : null}

              {info.phoneNumber ? (
                <div className="chat-user-details-row">
                  <span className="chat-user-details-label f-12">
                    {t("User Profile.Phone Number")}
                  </span>
                  <a
                    href={`tel:${info.phoneNumber}`}
                    className="chat-user-details-value f-12 link-style"
                  >
                    {info.phoneNumber}
                  </a>
                </div>
              ) : null}
            </div>
          )}

        </div>

        <div className="chat-user-details-footer">
          <Link
            to={`/User/${userUid}`}
            className="wljPrimaryBtn chat-user-details-profile-btn"
            onClick={onClose}
          >
            <PersonSharpIcon className="material-symbols-sharp" />
            <span>{t("My Connection.View Profile")}</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

export default ChatUserDetailsPanel;
